import React from "react";

import { Card, Spinner } from "react-bootstrap";
import { Button, Col, Container, Row } from "react-bootstrap";

import classNames from "classnames";
import { format } from "numerable";
import { Link } from "react-router-dom";

import { Participant } from "@api/graphql";
import { CsvSubmissionFormData } from "@pages/dashboard/ResaleCsvSubmission";

import { ROUTES } from "../../../routes";
import { FormErrorText } from "../../form/FormErrorText";
import { FormStepProps } from "../../form/MultiStepForm";
import { SubmitResalesDisclaimer } from "../SubmitResalesDisclaimer";

import styles from "./UploadReview.module.scss";

const FILE_SIZE_LIMIT = 10000000; // 10MB

interface UploadReviewProps extends FormStepProps<CsvSubmissionFormData, unknown> {
  isSubmitting: boolean;
  participant: Participant;
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const UploadReview = ({ multiFormData, onSubmit, isSubmitting, participant }: UploadReviewProps) => {
  let fileErrorMessage = undefined;
  const file: File | undefined = multiFormData?.csvInput?.file;

  const allowedFiletypes = [
    "text/csv",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ];

  const fileIsValid = () => {
    if (!file) {
      return false;
    }

    const fileName = file.name.toLowerCase();
    if (fileName.endsWith(".xls") && !fileName.endsWith(".xlsx")) {
      fileErrorMessage =
        "Legacy .xls files are not supported. Save the spreadsheet as .xlsx or .csv and upload it again.";
      return false;
    }
    const extensionOk = fileName.endsWith(".csv") || fileName.endsWith(".xlsx");
    if (!extensionOk && allowedFiletypes.indexOf(file.type) === -1) {
      fileErrorMessage = "The format is incorrect. Please upload a .csv or .xlsx file.";
      return false;
    }
    if (file.size > FILE_SIZE_LIMIT) {
      fileErrorMessage = "The file is too large. Please upload a file that is less than 10MB.";
      return false;
    } else {
      fileErrorMessage = undefined;
      return true;
    }
  };

  const fileSizeDisplay = () => {
    if (!fileIsValid() || !file) {
      return "Error";
    } else {
      return format(file.size, "0.00 bd");
    }
  };

  if (isSubmitting) {
    return (
      <Container fluid className={classNames(styles["container"])}>
        <Spinner>
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Row>
      <Col className={styles["cartSummary"]}>
        <Container fluid className={classNames(styles["container"])}>
          <Row>
            <Col>
              <h1>Upload Review</h1>
            </Col>
            {participant !== undefined ? (
              <h5>
                for <b>{participant.full_name}</b>
              </h5>
            ) : (
              <></>
            )}
          </Row>
          <Row className="mb-4">
            <Col className={classNames(styles["card-col"])}>
              <Card className={classNames(styles["card"])}>
                <Card.Text className={classNames(styles["card-header"])}>Uploaded File</Card.Text>
                <Row>
                  <Col>{file && <Card.Text>{file.name}</Card.Text>}</Col>
                  <Col>
                    <Card.Text className={classNames(styles["card-filesize"])}>{fileSizeDisplay()}</Card.Text>
                  </Col>
                </Row>
              </Card>
            </Col>
            {!fileIsValid() && (
              <>
                <FormErrorText error={fileErrorMessage}></FormErrorText>
                <Link to={ROUTES.ResaleCsvSubmission.path}>
                  <Button>Upload another file</Button>
                </Link>
              </>
            )}
          </Row>
        </Container>
      </Col>
      {fileIsValid() && (
        <Col className={classNames(styles["submit-panel"], "p-5")}>
          <SubmitResalesDisclaimer
            confirmationButtonText="Confirm and Upload"
            onConfirmation={() => onSubmit({})}
            inputsValid={fileIsValid()}
            isSubmitting={isSubmitting}
          />
        </Col>
      )}
    </Row>
  );
};
